import { useTask } from '@gleamer/ui';
import { useQueries } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useReports } from '../../../../contexts/ReportsContext';
import { removeRedundantSpaces, reportMentions } from './ReportDisplay';

/**
 * "Report mentions": the task's keywords the case's text reports contain, as amber chips above the annotation, so
 * the labeller sees what the report says without opening it. The reports are read through the same query as the
 * Report tab, so they are fetched once. Nothing when the task highlights nothing, hides the reports, or no text
 * report mentions a keyword (a PDF report has no text to read).
 */
export function ReportMentions() {
  const { t } = useTranslation();
  const task = useTask();
  const { patientReports = [], reports = {}, fetchReport } = useReports();

  const highlights = task?.reportHighlights ?? [];
  const textReports = task?.reportsHidden
    ? []
    : [...Object.values(reports).flat(), ...patientReports].filter(
        report => report?.contentType === 'text/plain'
      );

  const texts = useQueries({
    queries: textReports.map(report => ({
      queryKey: ['report', report.url],
      queryFn: async () =>
        removeRedundantSpaces(await (await fetchReport(report)).text()),
      staleTime: Infinity,
      enabled: highlights.length > 0,
    })),
  });

  const mentions = Array.from(
    new Set(
      texts.flatMap(({ data }) =>
        typeof data === 'string' ? reportMentions(data, highlights) : []
      )
    )
  );

  if (mentions.length === 0) {
    return null;
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 px-4 pt-3.5 text-xs text-dl-muted"
      data-testid="report-mentions"
    >
      {t('Report mentions')}
      {mentions.map(mention => (
        <span
          key={mention}
          className="rounded-md bg-dl-warning-tint px-2 py-0.5 font-semibold text-dl-warning"
        >
          {mention}
        </span>
      ))}
    </div>
  );
}
